'use client'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { createClient } from '@/lib/supabase/browser'
import { useSiteSettings } from '@/lib/useSiteSettings'
import { MEMBERS_ENABLED } from '@/lib/featureFlags'

/**
 * MemberButton — Nav entry for member system.
 * - Logged out: 登入 text link → /login
 * - Logged in:  avatar circle (initial) → click opens dropdown menu with
 *               訂單 / 收藏 / 預約 / 密碼設定 / 登出
 */
export default function MemberButton() {
  if (!MEMBERS_ENABLED) return null
  return <MemberButtonInner />
}

function MemberButtonInner() {
  const [user, setUser] = useState(null)
  const [loaded, setLoaded] = useState(false)
  const [open, setOpen] = useState(false)
  const [tier, setTier] = useState(null)
  const [activeBookings, setActiveBookings] = useState(0)
  const ref = useRef(null)
  // Phase 3.6 — member UI copy + tier names from CMS
  const { settings: m } = useSiteSettings('membership')

  useEffect(() => {
    const supabase = createClient()
    let cancelled = false

    async function bootstrap() {
      const { data } = await supabase.auth.getUser()
      if (cancelled) return
      setUser(data?.user ?? null)
      setLoaded(true)

      if (data?.user) {
        // Fetch tier + active bookings count for small badge
        const [{ data: profile }, { data: bookings }] = await Promise.all([
          supabase.from('profiles').select('tier').eq('user_id', data.user.id).maybeSingle(),
          supabase
            .from('bookings')
            .select('id, stage')
            .eq('user_id', data.user.id)
            .not('stage', 'in', '(completed,cancelled)'),
        ])
        if (cancelled) return
        if (profile?.tier) setTier(profile.tier)
        if (bookings) setActiveBookings(bookings.length)
      }
    }

    bootstrap()

    const { data: sub } = supabase.auth.onAuthStateChange(() => {
      bootstrap()
    })
    return () => {
      cancelled = true
      sub.subscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (!open) return
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open])

  if (!loaded) {
    // Reserve space while loading so layout doesn't jump
    return <div className="w-11 h-11" aria-hidden="true" />
  }

  if (!user) {
    return (
      <Link
        href="/login"
        className="flex items-center gap-1.5 px-3 h-11 text-xs tracking-wide rounded-full"
        style={{
          color: 'var(--color-text)',
          border: '1px solid var(--color-cardBorder)',
          fontFamily: 'var(--font-body)',
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
        {m.member_login_button || '登入'}
      </Link>
    )
  }

  const initial = ((user.user_metadata?.display_name || user.email || '?') + '').trim().charAt(0).toUpperCase()
  const email = user.email || ''
  const tierLabel = {
    bronze: m.tier_name_bronze || '銅',
    silver: m.tier_name_silver || '銀',
    gold: m.tier_name_gold || '金',
    platinum: m.tier_name_platinum || '白金',
  }[tier] || null

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="relative w-11 h-11 flex items-center justify-center rounded-full"
        aria-label={`會員選單${activeBookings ? ` (${activeBookings} 個進行中預約)` : ''}`}
        aria-expanded={open}
        aria-haspopup="menu"
        style={{
          border: '1px solid var(--color-cardBorder)',
        }}
      >
        <span
          className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold"
          style={{
            backgroundColor: 'var(--color-accent)',
            color: '#fff',
            fontFamily: 'var(--font-body)',
          }}
        >
          {initial}
        </span>
        {activeBookings > 0 && (
          <span
            aria-hidden="true"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center text-xs font-bold text-white leading-none px-1"
            style={{
              backgroundColor: '#E85D5D',
              borderRadius: '9px',
              border: '2px solid var(--color-bg)',
            }}
          >
            {activeBookings > 9 ? '9+' : activeBookings}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 py-1 shadow-lg"
          style={{
            backgroundColor: 'var(--color-cardBg)',
            border: '1px solid var(--color-cardBorder)',
            borderRadius: 'var(--radius-md)',
            minWidth: 220,
            zIndex: 70,
          }}
        >
          {/* Header: email + tier badge */}
          <div className="px-3 py-2 mb-1" style={{ borderBottom: '1px solid var(--color-cardBorder)' }}>
            <p className="text-xs truncate" style={{ color: 'var(--color-text)' }}>
              {email}
            </p>
            {tierLabel && (
              <p className="text-xs mt-0.5" style={{ color: 'var(--color-textMuted)' }}>
                {tierLabel}{m.member_tier_suffix || '級會員'}
              </p>
            )}
          </div>

          <MenuLink href="/member" onClick={() => setOpen(false)} icon="home">
            {m.member_center_label || '會員中心'}
          </MenuLink>
          <MenuLink href="/member/orders" onClick={() => setOpen(false)} icon="package">
            {m.member_orders_label || '我的訂單'}
          </MenuLink>
          <MenuLink href="/member/favorites" onClick={() => setOpen(false)} icon="heart">
            {m.member_favorites_label || '我的收藏'}
          </MenuLink>
          <MenuLink
            href="/member/bookings"
            onClick={() => setOpen(false)}
            icon="calendar"
            badge={activeBookings > 0 ? activeBookings : null}
          >
            {m.member_progress_label || '刺青進度'}
          </MenuLink>
          <MenuLink href="/member/password" onClick={() => setOpen(false)} icon="lock">
            {m.member_password_label || '密碼設定'}
          </MenuLink>

          <form action="/api/auth/logout" method="post" className="mt-1" style={{ borderTop: '1px solid var(--color-cardBorder)' }}>
            <button
              type="submit"
              className="w-full text-left px-3 py-2 text-sm flex items-center gap-2"
              style={{ color: 'var(--color-textSecondary)', fontFamily: 'var(--font-body)' }}
            >
              <IconByName name="logout" />
              {m.member_logout_button || '登出'}
            </button>
          </form>
        </div>
      )}
    </div>
  )
}

function MenuLink({ href, onClick, children, icon, badge }) {
  return (
    <Link
      href={href}
      onClick={onClick}
      role="menuitem"
      className="flex items-center gap-2 px-3 py-2 text-sm transition-colors hover:opacity-75"
      style={{ color: 'var(--color-text)', fontFamily: 'var(--font-body)' }}
    >
      <IconByName name={icon} />
      <span className="flex-1">{children}</span>
      {badge != null && (
        <span
          className="text-xs px-1.5 py-0.5 rounded-full font-semibold"
          style={{ backgroundColor: '#E85D5D', color: '#fff', minWidth: 20, textAlign: 'center' }}
        >
          {badge}
        </span>
      )}
    </Link>
  )
}

function IconByName({ name }) {
  const common = {
    width: 14,
    height: 14,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  }
  switch (name) {
    case 'home':
      return (
        <svg {...common}>
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      )
    case 'package':
      return (
        <svg {...common}>
          <path d="M16.5 9.4L7.5 4.21" />
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
          <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
          <line x1="12" y1="22.08" x2="12" y2="12" />
        </svg>
      )
    case 'heart':
      return (
        <svg {...common}>
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </svg>
      )
    case 'calendar':
      return (
        <svg {...common}>
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      )
    case 'lock':
      return (
        <svg {...common}>
          <rect x="3" y="11" width="18" height="11" rx="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      )
    case 'logout':
      return (
        <svg {...common}>
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <polyline points="16 17 21 12 16 7" />
          <line x1="21" y1="12" x2="9" y2="12" />
        </svg>
      )
    default:
      return null
  }
}
